import type {
  AckPayloads,
  AgentAccent,
  AgentUsingBase,
  AudienceInput,
  GroupRef,
  KnowledgeBase,
  LendingRequest
} from '@ralph/contracts'
import { ACKS, AgentAccentSchema, liveAudienceOf } from '@ralph/contracts'
import clsx from 'clsx'
import { Check, Globe, Hourglass, Info, Lock, MessageSquareText, TriangleAlert } from 'lucide-react'
import { type ReactNode, type RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { isStaleEdit } from '@/api/knowledge-bases.ts'
import { AudiencePicker } from '@/components/groups/AudiencePicker.tsx'
import { isUnchanged, reachLine, reachPending, reviewLine } from '@/components/groups/audience-picker.ts'
import { GroupAvatar } from '@/components/groups/GroupAvatar.tsx'
import { GroupChip, GroupLabel } from '@/components/groups/GroupLabel.tsx'
import { AgentAvatar } from '@/components/marketplace/consumer/AgentAvatar.tsx'
import { SectionHead } from '@/components/ui/admin-chrome.tsx'
import { Badge, Button, EmptyState, Spinner } from '@/components/ui/primitives.tsx'
import { useGroups, useReach } from '@/hooks/use-groups.ts'
import {
  useAgentsUsingBase,
  useBaseAccess,
  useChangeBaseAccess,
  useLendingRequest,
  useStopLending
} from '@/hooks/use-knowledge-bases.ts'
import { useWithdrawSharingRequest } from '@/hooks/use-sharing.ts'
import { useI18n } from '@/i18n/index.ts'
import {
  agentAnswering,
  agentMaintainers,
  agentStateLabel,
  canLetAnswer,
  foldersAskAgain,
  privateLabelFor,
  requestNotice,
  type SaveResult,
  saveResult,
  saveVerb
} from './knowledge-base.ts'
import { asSentence, messageOf, useKnowledgeViewer } from './knowledge-viewer.ts'
import { DeclineDialog, LetAnswerDialog } from './LendingDialogs.tsx'

/**
 * "Who can use it" on a knowledge base its maintainers open (PRD-26 D-13, D-16, D-40).
 *
 * Two halves, each opened by the one sentence that says what it decides:
 *
 *  - **Who can use it** — the people who can find it, open it, search it, and get answers from it
 *    through any agent they use. The shared picker, live from the base's own access; Save sends
 *    the choice with the sentences the maintainer read under it (`ackText`), the values those
 *    sentences were rendered from and the language they were read in (PRD-27 D-7), and the
 *    version they read (`ifVersion`). What they may change alone goes live; what needs an administrator becomes
 *    one sharing request, which waits here with *Withdraw request*. Each group it is shared with
 *    says who shared it, and who approved it.
 *  - **Agents that use it** — every agent it is attached to, how many people each reaches, and
 *    how many of those cannot use the base. An agent answers from it only for the people who can
 *    use it — unless its maintainers let it answer for everyone it reaches, a decision taken on the
 *    facts in a dialog, and one a request can ask for.
 */

/**
 * A notification about a request to let an agent answer from this base lands here with
 * `?lending=<requestId>` (the bell, PRD-26): the request is scrolled to and focused, so its
 * maintainer can decide it at once. One that stopped waiting — decided, withdrawn, closed — gets a
 * quiet line saying so instead, and the tab otherwise opens as it always does.
 */
export const AccessTab = ({ base, focusRequestId }: { base: KnowledgeBase; focusRequestId: string | null }) => {
  // Read inside the body, never captured at module scope: a capture taken at import freezes the
  // language and renders the previous one after a switch, with nothing failing (PRD-27 F-3).
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const agents = useAgentsUsingBase(base.id)
  const items = agents.data?.items ?? []
  const pending = items.reduce((count, entry) => count + entry.requests.length, 0)
  const waitingHere =
    focusRequestId !== null && items.some((entry) => entry.requests.some((request) => request.id === focusRequestId))
  // Only a request that is not waiting in a row is read on its own, to say what became of it.
  const lookup = useLendingRequest(focusRequestId !== null && agents.isSuccess && !waitingHere ? focusRequestId : null)
  const notice =
    focusRequestId !== null && agents.isSuccess
      ? requestNotice(fmt, { request: lookup.data ?? null, waitingHere, unreadable: lookup.isError }, fmt.formatAgo)
      : null
  const sectionRef = useRef<HTMLElement>(null)

  return (
    <div className="space-y-7">
      {notice && (
        <p
          role="status"
          className="flex items-start gap-2 rounded-tile border border-rule bg-card-solid/55 px-3.5 py-2.5 text-pretty text-[12.5px] leading-[1.5] text-fg-muted"
        >
          <Info size={13} strokeWidth={2.2} className="mt-[2px] shrink-0" aria-hidden />
          {notice}
        </p>
      )}

      {/* The tab opens with what its two halves decide, before either control. */}
      <dl className="grid gap-x-6 gap-y-3 rounded-tile border border-rule bg-fg/[0.02] px-4 py-3.5 sm:grid-cols-2">
        <div>
          <dt className="text-[13px] font-semibold tracking-[-0.014em]">{t.access.title}</dt>
          <dd className="mt-1 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{t.access.lead}</dd>
        </div>
        <div>
          <dt className="text-[13px] font-semibold tracking-[-0.014em]">{t.agents.title}</dt>
          <dd className="mt-1 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{t.agents.lead}</dd>
        </div>
      </dl>

      {/* Requests to decide sit below the picker; say so at the top, with the way down. */}
      {!waitingHere && pending > 0 && (
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-pretty text-[12.5px] leading-[1.45]">
          <MessageSquareText size={12} strokeWidth={2.2} className="shrink-0 text-primary" aria-hidden />
          <span className="min-w-0">{t.agents.waiting(fmt, pending)}</span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              sectionRef.current?.scrollIntoView({ block: 'start' })
              sectionRef.current?.focus({ preventScroll: true })
            }}
          >
            {t.agents.showRequests}
          </Button>
        </p>
      )}

      <WhoCanUseIt base={base} />
      <AgentsThatUseIt
        base={base}
        items={items}
        loading={agents.isLoading}
        failed={agents.isError}
        focusRequestId={waitingHere ? focusRequestId : null}
        sectionRef={sectionRef}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Who can use it
// ---------------------------------------------------------------------------

const WhoCanUseIt = ({ base }: { base: KnowledgeBase }) => {
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const groups = fmt.t.groups
  const viewer = useKnowledgeViewer()
  const access = useBaseAccess(base.id)
  const change = useChangeBaseAccess(base.id)
  const withdraw = useWithdrawSharingRequest()
  // The picker's own first page of groups (the same query, so the same cache): names for the
  // sentences read before saving, for groups chosen from it.
  const directory = useGroups({ limit: 200 })

  const [draft, setDraft] = useState<AudienceInput | null>(null)
  // The picker's own two sentences, as it shows them (`onStatus`).
  const [shown, setShown] = useState<{ reach: string; review: string | null } | null>(null)
  const [result, setResult] = useState<SaveResult | null>(null)
  const [stale, setStale] = useState(false)
  const [withdrawn, setWithdrawn] = useState(false)

  const view = access.data ?? null
  const live = view ? liveAudienceOf(view.access) : null
  const value: AudienceInput =
    draft ?? (live ? { kind: live.kind, groupIds: [...live.groupIds] } : { kind: 'private', groupIds: [] })
  const unchanged = live !== null && isUnchanged(live, value)
  const valid = !(value.kind === 'groups' && value.groupIds.length === 0)

  const pickerViewer = useMemo(
    () => ({
      isAdmin: viewer.isAdmin,
      groupIds: viewer.groups.map((group) => group.id),
      managedGroupIds: viewer.managedGroups.map((group) => group.id)
    }),
    [viewer]
  )
  const known = useMemo<GroupRef[]>(() => [...(view?.access.groups ?? []), ...(view?.managedGroups ?? [])], [view])
  const names = useMemo(
    () =>
      new Map(
        [...known, ...viewer.groups, ...viewer.managedGroups, ...(directory.data?.items ?? [])].map((group) => [
          group.id,
          group.name
        ])
      ),
    [known, viewer, directory.data]
  )

  // What is stored as read (`ackText`) is what was on the screen when Save was pressed (AGM-042):
  // the picker's own sentences, handed up through `onStatus`. The same rules are computed here too,
  // for the save button's verb and for the first render, before the picker has reported.
  const privateLabel = privateLabelFor(fmt, base.owner, viewer.id)
  const reachItem = { kind: 'knowledge_base' as const, id: base.id }
  const reach = useReach(valid ? value : null, reachItem)
  // Save waits for the count of *this* choice; the previous one's is not what anybody agrees to.
  const counting = valid && reachPending(reach)
  const review = reviewLine({ viewer: pickerViewer, live, requested: value, names })
  const reachText = groups.reach(
    fmt,
    counting
      ? { kind: 'pending' }
      : reachLine({ requested: value, people: reach.data?.people ?? null, current: reach.data?.current ?? null }),
    { privateLabel, consequence: groups.consequence.knowledge_base }
  )
  const reviewText = groups.review(fmt, review, 'save')
  const ackText = (shown ? [shown.reach, shown.review] : [reachText, reviewText])
    .filter((text): text is string => Boolean(text))
    .join(' ')
  /**
   * The same acknowledgement, structurally (PRD-27 D-7).
   *
   * `ackText` above is the EVIDENCE — the picker's own two sentences, exactly as they stood on
   * screen — and this is the RECORD: the values those sentences were rendered from, which the
   * picker's catalogue entries can be handed again, in either language, to say the same thing to
   * an auditor who does not read Portuguese. `reach` and `review` are the picker's own line
   * shapes, so the assignment here is what catches any drift between them and the contract.
   */
  const ackParams: AckPayloads['ack.base_access'] = {
    base: base.name,
    reach: counting
      ? { kind: 'pending' }
      : reachLine({ requested: value, people: reach.data?.people ?? null, current: reach.data?.current ?? null }),
    review,
    privateOwner:
      base.owner.kind === 'group'
        ? { kind: 'group_managers', name: base.owner.group.name }
        : base.owner.id === viewer.id
          ? { kind: 'self', name: null }
          : { kind: 'person', name: base.owner.name }
  }
  const verb = saveVerb(review)
  const foldersAsk =
    unchanged || base.connectedFolders.total === 0
      ? null
      : foldersAskAgain(live, value, { isAdmin: viewer.isAdmin, managedGroupIds: pickerViewer.managedGroupIds })

  const save = () => {
    if (!view || unchanged || !valid || counting || change.isPending) return
    setResult(null)
    setStale(false)
    setWithdrawn(false)
    change.mutate(
      {
        access: value,
        ackText,
        ifVersion: view.version,
        ackKey: ACKS.baseAccess.key,
        ackVersion: ACKS.baseAccess.version,
        ackLocale: fmt.locale,
        ackParams
      },
      {
        onSuccess: (outcome) => {
          setResult(saveResult(outcome))
          setDraft(null)
        },
        onError: (error) => {
          if (!isStaleEdit(error)) return
          // Somebody else's change landed first: show theirs, and let this maintainer decide again.
          setStale(true)
          setDraft(null)
          void access.refetch()
        }
      }
    )
  }

  return (
    <section aria-label={t.access.title} className="space-y-3.5">
      <SectionHead title={t.access.title} lead={viewer.isAdmin ? t.access.sectionLeadAdmin : t.access.sectionLead} />

      {access.isLoading ? (
        <p className="flex items-center gap-2 text-[13px] text-fg-subtle">
          <Spinner size={12} /> {t.access.loading}
        </p>
      ) : access.isError || !view ? (
        <EmptyState title={t.access.failed} body={t.access.failedBody} />
      ) : (
        <>
          {view.pendingRequest && (
            <div className="rounded-tile border border-rule-strong bg-card-solid/55 px-3.5 py-3">
              <p className="flex items-center gap-1.5 text-[13px] font-semibold">
                <Hourglass size={13} strokeWidth={2.2} className="shrink-0 text-primary" aria-hidden />
                {t.access.pendingTitle}
              </p>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[12.5px] text-fg-muted">
                {view.pendingRequest.requestedKind === 'everyone' ? (
                  <span>{t.access.pendingEveryone}</span>
                ) : (
                  <>
                    <span>{t.access.pendingGroups}</span>
                    {view.pendingRequest.groups.map((group) => (
                      <GroupChip key={group.id} group={group} />
                    ))}
                  </>
                )}
              </div>
              <p className="mt-1.5 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
                {t.access.pendingBody(fmt.formatAgo(view.pendingRequest.requestedAt))}
              </p>
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  disabled={withdraw.isPending}
                  onClick={() => {
                    if (!view.pendingRequest) return
                    setResult(null)
                    withdraw.mutate(view.pendingRequest.id, { onSuccess: () => setWithdrawn(true) })
                  }}
                >
                  {withdraw.isPending ? t.access.withdrawing : t.access.withdraw}
                </Button>
                {withdraw.isError && (
                  <span role="alert" className="text-[12.5px] text-alert">
                    {asSentence(messageOf(withdraw.error, t.genericError))}
                  </span>
                )}
              </div>
            </div>
          )}

          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault()
              save()
            }}
          >
            <AudiencePicker
              mode="item"
              value={value}
              onChange={(next) => {
                setDraft(next)
                setResult(null)
                setStale(false)
                setWithdrawn(false)
              }}
              live={live}
              known={known}
              viewer={pickerViewer}
              privateLabel={privateLabel}
              reachItem={reachItem}
              consequence={groups.consequence.knowledge_base}
              commitVerb="save"
              disabled={change.isPending}
              onStatus={setShown}
            />

            {foldersAsk !== null && (
              <p className="text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
                {foldersAsk === 'now'
                  ? t.access.foldersAskAgain(fmt, base.connectedFolders.total)
                  : t.access.foldersAskAfterApproval(fmt, base.connectedFolders.total)}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button type="submit" variant="primary" disabled={unchanged || !valid || counting || change.isPending}>
                {change.isPending
                  ? t.access.saving
                  : verb === 'send'
                    ? t.access.sendForReview
                    : verb === 'save_and_send'
                      ? t.access.saveAndSend
                      : t.access.save}
              </Button>
              {draft !== null && !unchanged && (
                <Button variant="ghost" disabled={change.isPending} onClick={() => setDraft(null)}>
                  {t.access.discard}
                </Button>
              )}
            </div>
          </form>

          {/* Mounted empty, so the outcome of a save is announced the moment it lands. */}
          <div role="status" className="text-[12.5px] leading-[1.5]">
            {result && <SaveOutcome result={result} />}
            {withdrawn && <p className="text-fg-muted">{t.access.withdrawn}</p>}
          </div>
          {stale && (
            <p role="alert" className="flex items-start gap-1.5 text-pretty text-[12.5px] leading-[1.5] text-alert">
              <TriangleAlert size={12} strokeWidth={2.2} className="mt-[3px] shrink-0" aria-hidden />
              {t.access.stale}
            </p>
          )}
          {change.isError && !isStaleEdit(change.error) && (
            <p role="alert" className="text-pretty text-[12.5px] leading-[1.5] text-alert">
              {asSentence(messageOf(change.error, t.genericError))}
            </p>
          )}

          {view.sharedBy.length > 0 && (
            <div>
              <h4 className="eyebrow mb-2">{t.access.sharedNow}</h4>
              <ul className="space-y-1.5">
                {view.sharedBy.map((entry) => (
                  <li key={entry.group.id} className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[12.5px]">
                    <GroupLabel group={entry.group} size={20} to className="font-medium" />
                    <span className="readout text-fg-subtle">
                      {t.access.sharedBy(entry.sharedByName, entry.byAdministrator, fmt.formatAgo(entry.sharedAt))}
                      {entry.approvedByName ? ` · ${t.access.approvedBy(entry.approvedByName)}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  )
}

const SaveOutcome = ({ result }: { result: SaveResult }) => {
  const t = useI18n().t.knowledge
  if (result.kind === 'nothing') return <p className="text-fg-muted">{t.access.nothingChanged}</p>
  if (result.kind === 'applied') {
    return (
      <p className="flex items-center gap-1.5 text-fg">
        <Check size={12} strokeWidth={2.4} className="shrink-0 text-grounded" aria-hidden />
        {t.access.saved}
      </p>
    )
  }
  return (
    <div className="space-y-1.5">
      <p className="flex items-center gap-1.5 text-fg">
        {result.kind === 'applied_and_sent' ? (
          <Check size={12} strokeWidth={2.4} className="shrink-0 text-grounded" aria-hidden />
        ) : (
          <Hourglass size={12} strokeWidth={2.2} className="shrink-0 text-primary" aria-hidden />
        )}
        {result.kind === 'applied_and_sent' ? t.access.savedAndSent : t.access.sentForReview}
      </p>
      <div className="flex flex-wrap items-center gap-1.5 text-fg-muted">
        <span>{t.access.waitingFor}</span>
        {result.everyone ? (
          <span className="font-medium text-fg">{t.access.waitingEveryone}</span>
        ) : (
          result.groups.map((group) => <GroupChip key={group.id} group={group} />)
        )}
      </div>
      <p className="text-fg-muted">{t.access.reviewNote}</p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Agents that use it
// ---------------------------------------------------------------------------

type Decision =
  | { kind: 'let'; entry: AgentUsingBase }
  | { kind: 'allow'; entry: AgentUsingBase; request: LendingRequest }
  | { kind: 'decline'; entry: AgentUsingBase; request: LendingRequest }

const AgentsThatUseIt = ({
  base,
  items,
  loading,
  failed,
  focusRequestId,
  sectionRef
}: {
  base: KnowledgeBase
  items: AgentUsingBase[]
  loading: boolean
  failed: boolean
  /** A request waiting in one of the rows that a notification pointed at. */
  focusRequestId: string | null
  sectionRef: RefObject<HTMLElement | null>
}) => {
  const t = useI18n().t.knowledge
  const [decision, setDecision] = useState<Decision | null>(null)

  return (
    <section
      ref={sectionRef}
      tabIndex={-1}
      aria-label={t.agents.title}
      className="space-y-3.5 border-t border-rule pt-6 focus:outline-none"
    >
      <SectionHead title={t.agents.title} lead={t.agents.sectionLead} />

      {loading ? (
        <p className="flex items-center gap-2 text-[13px] text-fg-subtle">
          <Spinner size={12} /> {t.agents.loading}
        </p>
      ) : failed ? (
        <EmptyState title={t.agents.failed} body={t.access.failedBody} />
      ) : items.length === 0 ? (
        <EmptyState title={t.agents.empty} body={t.agents.emptyBody} />
      ) : (
        <ul className="space-y-2">
          {items.map((entry) => (
            <AgentRow
              key={entry.agent.id}
              base={base}
              entry={entry}
              focusRequestId={focusRequestId}
              onLet={() => setDecision({ kind: 'let', entry })}
              onAllow={(request) => setDecision({ kind: 'allow', entry, request })}
              onDecline={(request) => setDecision({ kind: 'decline', entry, request })}
            />
          ))}
        </ul>
      )}

      {decision?.kind === 'let' && (
        <LetAnswerDialog base={base} entry={decision.entry} onClose={() => setDecision(null)} />
      )}
      {decision?.kind === 'allow' && (
        <LetAnswerDialog
          base={base}
          entry={decision.entry}
          request={decision.request}
          onClose={() => setDecision(null)}
        />
      )}
      {decision?.kind === 'decline' && (
        <DeclineDialog
          base={base}
          entry={decision.entry}
          request={decision.request}
          onClose={() => setDecision(null)}
        />
      )}
    </section>
  )
}

const accentOf = (accent: string): AgentAccent => {
  const parsed = AgentAccentSchema.safeParse(accent)
  return parsed.success ? parsed.data : 'slate'
}

const AgentRow = ({
  base,
  entry,
  focusRequestId,
  onLet,
  onAllow,
  onDecline
}: {
  base: KnowledgeBase
  entry: AgentUsingBase
  focusRequestId: string | null
  onLet: () => void
  onAllow: (request: LendingRequest) => void
  onDecline: (request: LendingRequest) => void
}) => {
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const stop = useStopLending(base.id)
  const [confirmStop, setConfirmStop] = useState(false)
  const { agent } = entry
  const answering = agentAnswering(entry)
  const state = agentStateLabel(fmt, agent.state)
  const forAgent = <span className="sr-only">{t.agents.forAgent(agent.name)}</span>

  return (
    <li className="rounded-tile border border-rule bg-card-solid/55 px-3.5 py-3">
      <div className="flex flex-wrap items-start gap-3">
        <AgentAvatar displayName={agent.name} icon={agent.icon} accent={accentOf(agent.accent)} size="md" />
        <div className="min-w-0 flex-1 basis-[14rem]">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Link
              to={`/agents/${encodeURIComponent(agent.id)}`}
              className="truncate text-[13.5px] font-semibold tracking-[-0.014em] underline decoration-transparent underline-offset-2 transition-colors duration-200 hover:decoration-current"
            >
              {agent.name}
            </Link>
            {state !== '' && <Badge>{state}</Badge>}
          </p>
          <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[12.5px] text-fg-muted">
            {agent.owner.kind === 'group' && <GroupAvatar group={agent.owner.group} size={16} />}
            <span className="truncate">{t.agents.maintainedBy(agentMaintainers(fmt, agent.owner))}</span>
          </p>
          <p className="readout mt-1 text-fg-subtle">
            {agent.state === 'published'
              ? t.agents.reaches(fmt, agent.reach, entry.beyondAccess, base.name)
              : t.agents.notLive(agent.state)}
          </p>
          <p className="mt-1.5 flex items-start gap-1.5 text-[12.5px] leading-[1.45]">
            {answering === 'everyone' ? (
              <Globe size={12} strokeWidth={2.2} className="mt-[3px] shrink-0 text-primary" aria-hidden />
            ) : answering === 'detached' ? (
              <TriangleAlert size={12} strokeWidth={2.2} className="mt-[3px] shrink-0 text-fg-muted" aria-hidden />
            ) : (
              <Lock size={12} strokeWidth={2.2} className="mt-[3px] shrink-0 text-fg-subtle" aria-hidden />
            )}
            <span>
              {answering === 'users' ? (
                <span className="text-fg-muted">{t.agents.onlyUsers}</span>
              ) : answering === 'detached' ? (
                <span className="text-fg-muted">{t.agents.detached}</span>
              ) : (
                <>
                  <span className="font-medium">{t.agents.forEveryone}</span>
                  {entry.lending && (
                    <span className="readout ml-1.5 text-fg-subtle">
                      {t.agents.allowedBy(
                        entry.lending.grantedBy?.displayName ?? null,
                        entry.lending.byAdministrator,
                        fmt.formatAgo(entry.lending.grantedAt)
                      )}
                    </span>
                  )}
                </>
              )}
            </span>
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          {canLetAnswer(entry) && entry.requests.length === 0 && (
            <Button size="sm" variant={entry.beyondAccess > 0 ? 'secondary' : 'ghost'} onClick={onLet}>
              {t.agents.letAnswer}
              {forAgent}
            </Button>
          )}
          {entry.lending && !confirmStop && (
            <Button size="sm" variant="ghost" onClick={() => setConfirmStop(true)}>
              {t.agents.stop}
              {forAgent}
            </Button>
          )}
        </div>
      </div>

      {confirmStop && (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-tile bg-alert-soft px-3 py-2.5">
          <p className="min-w-[14rem] flex-1 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
            {t.agents.stopConfirm(agent.name, base.name)}
          </p>
          <span className="flex shrink-0 items-center gap-1.5">
            <Button
              size="sm"
              variant="danger"
              autoFocus
              disabled={stop.isPending}
              onClick={() => stop.mutate(agent.id, { onSuccess: () => setConfirmStop(false) })}
            >
              {stop.isPending ? t.agents.stopping : t.agents.stop}
              {forAgent}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmStop(false)}>
              {t.agents.keep}
            </Button>
          </span>
          {stop.isError && (
            <p role="alert" className="w-full text-[12.5px] text-alert">
              {asSentence(messageOf(stop.error, t.genericError))}
            </p>
          )}
        </div>
      )}

      {entry.requests.map((request) => (
        <RequestCard
          key={request.id}
          request={request}
          focused={request.id === focusRequestId}
          forAgent={forAgent}
          onAllow={() => onAllow(request)}
          onDecline={() => onDecline(request)}
        />
      ))}
    </li>
  )
}

/**
 * One request to let the agent answer for everyone it reaches, folded into its agent's row. The
 * one a notification pointed at is scrolled to, focused (so its name is read out first) and
 * drawn in the primary colour, so the decision is one Tab away.
 */
const RequestCard = ({
  request,
  focused,
  forAgent,
  onAllow,
  onDecline
}: {
  request: LendingRequest
  focused: boolean
  forAgent: ReactNode
  onAllow: () => void
  onDecline: () => void
}) => {
  const { formatAgo, t: messages } = useI18n()
  const t = messages.knowledge
  const ref = useRef<HTMLElement>(null)
  useEffect(() => {
    if (!focused) return
    ref.current?.scrollIntoView({ block: 'center' })
    ref.current?.focus({ preventScroll: true })
  }, [focused])

  return (
    <article
      ref={ref}
      aria-label={t.agents.requestLabel(request.requestedBy.displayName)}
      tabIndex={focused ? -1 : undefined}
      className={clsx(
        'mt-3 rounded-tile border px-3 py-2.5 focus:outline-none',
        focused ? 'border-primary bg-primary-soft shadow-glass' : 'border-rule-strong bg-card-solid'
      )}
    >
      <p className="flex items-start gap-1.5 text-[12.5px] leading-[1.45]">
        <MessageSquareText size={12} strokeWidth={2.2} className="mt-[3px] shrink-0 text-primary" aria-hidden />
        <span>
          {t.agents.asked(request.requestedBy.displayName, request.origin)}
          <span className="readout ml-1.5 text-fg-subtle">{formatAgo(request.createdAt)}</span>
        </span>
      </p>
      {request.note.trim() !== '' && (
        <blockquote className="mt-1.5 border-l-2 border-rule-strong pl-2.5 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
          {request.note}
        </blockquote>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <Button size="sm" variant="primary" onClick={onAllow}>
          {t.agents.allow}
          {forAgent}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDecline}>
          {t.agents.decline}
          {forAgent}
        </Button>
      </div>
    </article>
  )
}
